package org.example.project.screens_cus_mobile.wa

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.screens_cus_mobile.loadMobileErlaubteStandorte
import org.example.project.screens_cus_mobile.mobileStandortErlaubt
import org.example.project.service.YardApi
import org.example.project.service.YardEngineApi
import org.example.project.service.YardLiveSync
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun CUSMobileWAStandortKontrolleScreen() {
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var cards by remember { mutableStateOf<List<All>>(emptyList()) }
    var selected by remember { mutableStateOf<All?>(null) }
    var checkedKeys by remember(selected?.module?.firstOrNull()?.id) { mutableStateOf<Set<String>>(emptySet()) }
    var saving by remember { mutableStateOf(false) }
    val refreshVersion = YardLiveSync.version()

    suspend fun ladeDaten() {
        loading = true
        val erlaubteStandorte = loadMobileErlaubteStandorte()
        val palettenDefs = YardApi.loadPalettenDefinitionen().filter { it.aktivWa }

        val standorte = YardApi.loadWaListe()
            .flatMap { kopf ->
                YardApi.loadWaStandorteByTour(kopf.lieferscheinnummer)
                    .filter { it.zustand != ZustandManager.STORNIERT && it.zustand != ZustandManager.ABGESCHLOSSEN }
                    .filter { mobileStandortErlaubt(it, erlaubteStandorte) }
                    .map { standort -> standort to kopf }
            }
        val kontrollierteStandorte = standorte
            .filter { (standort, _) -> standort.kontrolliert }
            .mapNotNull { (standort, _) -> mobileWaStandortKontrollKey(standort).takeIf { it.isNotBlank() } }
            .toSet()

        cards = standorte
            .filter { (standort, _) -> !standort.kontrolliert }
            .filter { (standort, _) ->
                val standortKey = mobileWaStandortKontrollKey(standort)
                standortKey.isBlank() || standortKey !in kontrollierteStandorte
            }
            .map { (standort, kopf) ->
                mobileWaStandortKontrollCard(
                    standort = standort,
                    kopf = kopf,
                    pruefwerte = mobileWaStandortPruefwerte(standort, palettenDefs)
                )
            }
            .sortedWith(
                compareBy<All> { it.mobileWaStandortKontrollKopf().lieferscheinnummer }
                    .thenBy { it.mobileWaStandortKontrollStandort().ablaufPrio.takeIf { prio -> prio > 0 } ?: Int.MAX_VALUE }
                    .thenBy { it.mobileWaStandortKontrollStandort().id.toIntOrNull() ?: Int.MAX_VALUE }
            )
        loading = false
    }

    LaunchedEffect(refreshVersion) {
        ladeDaten()
    }

    suspend fun abschliessen(card: All) {
        val standort = card.mobileWaStandortKontrollStandort()
        YardEngineApi.waKontrolle(
            kopf = card.mobileWaStandortKontrollKopf(),
            standort = standort,
            firmenId = card.mobileWaStandortKontrollKopf().firmenId.ifBlank { standort.firmenId }
        )
        selected = null
        checkedKeys = emptySet()
        ladeDaten()
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(16.px)
            padding(12.px)
        }
    }) {
        selected?.let { card ->
            MobileWaStandortKontrolleDetail(
                card = card,
                checkedKeys = checkedKeys,
                saving = saving,
                onToggle = { key ->
                    checkedKeys = if (key in checkedKeys) checkedKeys - key else checkedKeys + key
                },
                onBestaetigen = {
                    if (saving) return@MobileWaStandortKontrolleDetail
                    saving = true
                    scope.launch {
                        abschliessen(card)
                        saving = false
                    }
                },
                onZurueck = {
                    selected = null
                    checkedKeys = emptySet()
                }
            )
        } ?: run {
            MobileWaStandortKontrolleHeader(offen = cards.size, loading = loading)
            when {
                loading -> MobileWaStandortKontrolleEmpty("WA-Kontrollen werden geladen...")
                cards.isEmpty() -> MobileWaStandortKontrolleEmpty("Keine offenen WA-Kontrollen vorhanden")
                else -> cards.forEach { card ->
                    MobileWaStandortKontrolleCard(card = card) {
                        selected = card
                        checkedKeys = emptySet()
                    }
                }
            }
        }
    }
}

@Composable
private fun MobileWaStandortKontrolleHeader(offen: Int, loading: Boolean) {
    Div({
        style {
            padding(18.px)
            borderRadius(8.px)
            background("linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%), ${DashboardTheme.PREMIUM_SURFACE_BG}")
            property("border", "1px solid rgba(255,255,255,0.10)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(12.px)
            textAlign("center")
        }
    }) {
        H3({ style { margin(0.px); color(DashboardTheme.TEXT_LIGHT); fontSize(20.px) } }) {
            Text("WA Kontrolle")
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                gap(10.px)
            }
        }) {
            Img(src = "images/kontrolliert.svg") {
                style {
                    width(34.px)
                    height(34.px)
                    property("filter", "brightness(1.8)")
                }
            }
            Span({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(24.px); fontWeight("800") } }) {
                Text(if (loading) "..." else offen.toString())
            }
        }
    }
}

@Composable
private fun MobileWaStandortKontrolleCard(card: All, onOpen: () -> Unit) {
    val standort = card.mobileWaStandortKontrollStandort()
    val kopf = card.mobileWaStandortKontrollKopf()
    Div({
        style {
            padding(16.px)
            borderRadius(8.px)
            backgroundColor(DashboardTheme.SURFACE_CARD)
            property("border", "1px solid rgba(255,255,255,0.08)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
            property("box-shadow", "0 12px 28px rgba(0,0,0,0.18)")
            cursor("pointer")
        }
        onClick { onOpen() }
    }) {
        MobileWaStandortKontrolleInfoRow("LS Nummer", standort.lieferscheinnummer.ifBlank { kopf.lieferscheinnummer }.ifBlank { "-" })
        MobileWaStandortKontrolleInfoRow("WA Platz", mobileWaStandortPlatz(standort))
        MobileWaStandortKontrolleInfoRow("Startverladung", mobileWaStandortStartverladung(standort))
        MobileWaStandortKontrolleInfoRow("Paletten", mobileWaStandortPalettenSumme(card).toString())
    }
}

@Composable
private fun MobileWaStandortKontrolleDetail(
    card: All,
    checkedKeys: Set<String>,
    saving: Boolean,
    onToggle: (String) -> Unit,
    onBestaetigen: () -> Unit,
    onZurueck: () -> Unit
) {
    val standort = card.mobileWaStandortKontrollStandort()
    val allChecked = card.preise.all { it.key in checkedKeys }
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(14.px)
        }
    }) {
        MobileWaStandortKontrolleCard(card = card, onOpen = {})
        card.preise.forEach { pruefwert ->
            Div({
                style {
                    padding(14.px)
                    borderRadius(8.px)
                    backgroundColor(DashboardTheme.SURFACE_CARD)
                    property("border", "1px solid rgba(255,255,255,0.08)")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.SpaceBetween)
                    gap(12.px)
                }
            }) {
                Span({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(16.px); fontWeight("800"); lineHeight("1.3") } }) {
                    Text("${pruefwert.label}: ${pruefwert.menge.ifBlank { "0" }}")
                }
                LumaticaCheckbox(
                    checked = pruefwert.key in checkedKeys,
                    onChange = { onToggle(pruefwert.key) },
                    fontSizePx = 28
                )
            }
        }
        if (card.preise.isEmpty()) {
            MobileWaStandortKontrolleEmpty("Keine Palettenmengen für ${mobileWaStandortPlatz(standort)} vorhanden.")
        }
        Div({ style { display(DisplayStyle.Flex); gap(10.px); width(100.percent) } }) {
            Div({ style { flex(1) } }) {
                LumaticaButton("Zurück", onZurueck, variant = LumaticaButtonVariant.Standard, heightPx = 42, enabled = !saving)
            }
            Div({ style { flex(1) } }) {
                LumaticaButton(
                    if (saving) "Speichere..." else "Mengen stimmen",
                    onBestaetigen,
                    variant = LumaticaButtonVariant.Action,
                    heightPx = 42,
                    enabled = !saving && allChecked && card.preise.isNotEmpty()
                )
            }
        }
    }
}

@Composable
private fun MobileWaStandortKontrolleEmpty(text: String) {
    Div({
        style {
            padding(16.px)
            color(DashboardTheme.TEXT_MUTED)
            fontSize(15.px)
            fontWeight("700")
            textAlign("center")
        }
    }) {
        Text(text)
    }
}

@Composable
private fun MobileWaStandortKontrolleInfoRow(label: String, value: String) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(3.px) } }) {
        Span({
            style {
                color(rgba(255, 255, 255, 0.48))
                fontSize(11.px)
                fontWeight("700")
                property("letter-spacing", "0.08em")
                property("text-transform", "uppercase")
            }
        }) { Text(label) }
        Span({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(14.px); lineHeight("1.35"); property("word-break", "break-word") } }) {
            Text(value.ifBlank { "-" })
        }
    }
}

private fun mobileWaStandortKontrollCard(standort: All, kopf: All, pruefwerte: List<All>): All =
    All(
        id = standort.id,
        lieferscheinnummer = standort.lieferscheinnummer.ifBlank { kopf.lieferscheinnummer },
        module = listOf(standort, kopf),
        preise = pruefwerte
    )

private fun All.mobileWaStandortKontrollStandort(): All = module.getOrNull(0) ?: All()

private fun All.mobileWaStandortKontrollKopf(): All = module.getOrNull(1) ?: All()

private fun mobileWaStandortPruefwerte(standort: All, palettenDefs: List<All>): List<All> {
    val defs = palettenDefs.ifEmpty {
        standort.palettenWerte.keys.map { All(id = it, bezeichnung = it, label = it, key = it) }
    }
    return defs.mapNotNull { def ->
        val label = def.bezeichnung.ifBlank { def.label.ifBlank { def.id } }
        val menge = standort.palettenWerte[label]?.toIntOrNull() ?: 0
        if (menge <= 0) return@mapNotNull null
        All(
            id = def.id.ifBlank { label },
            key = def.id.ifBlank { label },
            label = label,
            bezeichnung = label,
            menge = menge.toString()
        )
    }
}

private fun mobileWaStandortPlatz(standort: All): String =
    standort.platzcode.ifBlank { standort.platz.ifBlank { standort.standort.ifBlank { "-" } } }

private fun mobileWaStandortKontrollKey(standort: All): String =
    listOf(
        standort.platzcode,
        standort.platz,
        standort.standort,
        standort.slotStandortId,
        standort.waStandortId,
        standort.id
    )
        .firstOrNull { it.isNotBlank() }
        ?.trim()
        ?.lowercase()
        .orEmpty()

private fun mobileWaStandortStartverladung(standort: All): String =
    standort.verladeuhrzeit.ifBlank { standort.uhrzeitVon.ifBlank { standort.uhrzeitBis.ifBlank { standort.uhrzeitAnmeldung.ifBlank { "-" } } } }

private fun mobileWaStandortPalettenSumme(card: All): Int =
    card.preise.sumOf { it.menge.toIntOrNull() ?: 0 }
